import { z } from 'zod';
import { capitalizeIfUppercase, lowercaseIfUppercase } from './createApplication.model';

export type PartnerApplication = z.TypeOf<typeof PartnerApplicationSchema>;

const moneyStr = () =>
  z.string().optional().transform((str) => (str ? parseInt(str.replace(/,/g, ''), 10) : undefined));

export const PartnerApplicationSchema = z.object({
  // step #1
  firstName: z.string().optional().transform(capitalizeIfUppercase),
  middleName: z.string().optional().transform(capitalizeIfUppercase),
  lastName: z.string().optional().transform(capitalizeIfUppercase),
  suffix: z.string().optional(),
  emailAddress: z.string().optional().transform(lowercaseIfUppercase),
  maritalStatus: z.string().optional(),
  birthdate: z.string().optional(),
  primaryPhoneType: z.string().optional(),
  primaryPhoneNumber: z.string().optional(),
  ssn: z.string().optional(),

  // step #2
  residentialAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  residentialAddressLine2: z.string().optional().transform(capitalizeIfUppercase),
  residentialCity: z.string().optional().transform(capitalizeIfUppercase),
  residentialState: z.string().optional(),
  residentialZipCode: z.string().optional(),
  yearsAtResidentialAddress: z.coerce.number().optional(),
  monthsAtResidentialAddress: z.coerce.number().optional(),
  statusAtResidentialAddress: z.string().optional(),
  monthlyPayment: z.string().optional().transform((str) => (str ? parseInt(str.replace(/,/g, ''), 10) : undefined)),

  previousAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  previousAddressLine2: z.string().optional().transform(capitalizeIfUppercase),
  previousCity: z.string().optional().transform(capitalizeIfUppercase),
  previousState: z.string().optional(),
  previousZipCode: z.string().optional(),
  previousYearsAtAddress: z.coerce.number().min(0).max(99).optional(),
  previousMonthsAtAddress: z.coerce.number().min(0).max(11).optional(),

  isRegistrationAddress: z.coerce.boolean(),
  registrationAddress: z.string().optional(),
  isMailingAddress: z.coerce.boolean(),
  mailingAddress: z.string().optional(),

  // step #3
  isRetired: z.coerce.boolean(),
  retirementIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),
  retirementYears: z.coerce.number().min(0).max(99).optional(),
  retirementMonths: z.coerce.number().min(0).max(11).optional(),

  isEmployed: z.coerce.boolean(),
  employerName: z.string().optional().transform(capitalizeIfUppercase),
  employerPhoneNumber: z.string().optional(),
  occupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  grossMonthlyIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),
  employerAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  employerCity: z.string().optional().transform(capitalizeIfUppercase),
  employerState: z.string().optional(),
  employerZipCode: z.string().optional(),
  employedYears: z.coerce.number().min(0).max(99).optional(),
  employedMonths: z.coerce.number().min(0).max(11).optional(),

  previousEmployerName: z.string().optional().transform(capitalizeIfUppercase),
  previousOccupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  previousGrossMonthlyIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),
  previousEmployedYears: z.coerce.number().min(0).max(99).optional(),
  previousEmployedMonths: z.coerce.number().min(0).max(11).optional(),

  isAdditionalIncome: z.coerce.boolean(),
  additionalIncomeSource: z.string().optional(),
  additionalIncomeSourceOther: z.string().optional().transform(capitalizeIfUppercase),
  additionalMonthlyIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),

  haveCoApplicant: z.coerce.boolean(),
  coapplicantFirstName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantMiddleName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantLastName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantSuffix: z.string().optional(),
  coapplicantPrimaryPhoneType: z.string().optional(),
  coapplicantPrimaryPhoneNumber: z.string().optional(),
  coapplicantEmailAddress: z.string().optional().transform(lowercaseIfUppercase),
  coapplicantMaritalStatus: z.string().optional(),
  coapplicantSsn: z.string().optional(),
  coapplicantBirthdate: z.string().optional(),
  coapplicantResidentialAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantResidentialCity: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantResidentialState: z.string().optional(),
  coapplicantResidentialZipCode: z.string().optional(),
  coapplicantYearsAtResidentialAddress: z.coerce.number().min(0).max(99).optional(),
  coapplicantMonthsAtResidentialAddress: z.coerce.number().min(0).max(11).optional(),
  coapplicantStatusAtResidentialAddress: z.string().optional(),
  coapplicantMonthlyPayment: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),

  coapplicantPreviousAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousCity: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousState: z.string().optional(),
  coapplicantPreviousZipCode: z.string().optional(),
  coapplicantPreviousYearsAtAddress: z.coerce.number().min(0).max(99).optional(),
  coapplicantPreviousMonthsAtAddress: z.coerce.number().min(0).max(11).optional(),

  coapplicantIsRetired: z.coerce.boolean(),
  coapplicantRetirementIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),
  coapplicantRetirementYears: z.coerce.number().min(0).max(99).optional(),
  coapplicantRetirementMonths: z.coerce.number().min(0).max(11).optional(),

  coapplicantIsEmployed: z.coerce.boolean(),
  coapplicantEmployerName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantOccupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantGrossMonthlyIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),
  coapplicantEmployerAddressLine1: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantEmployerCity: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantEmployerState: z.string().optional(),
  coapplicantEmployerZipCode: z.string().optional(),
  coapplicantEmployerPhoneNumber: z.string().optional(),
  coapplicantEmployedYears: z.coerce.number().min(0).max(99).optional(),
  coapplicantEmployedMonths: z.coerce.number().min(0).max(11).optional(),

  coapplicantPreviousEmployerName: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousOccupationTitle: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantPreviousGrossMonthlyIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),
  coapplicantPreviousEmployedYears: z.coerce.number().min(0).max(99).optional(),
  coapplicantPreviousEmployedMonths: z.coerce.number().min(0).max(11).optional(),

  coapplicantIsAdditionalIncome: z.coerce.boolean(),
  coapplicantAdditionalIncomeSource: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantAdditionalIncomeSourceOther: z.string().optional().transform(capitalizeIfUppercase),
  coapplicantAdditionalMonthlyIncome: z.string().transform((str) => parseInt(str.replace(/,/g, ''), 10)).optional(),

  // step #4
  sellerType: z.string().optional(),
  sellerName: z.string().optional().transform(capitalizeIfUppercase),
  sellerPhoneNumber: z.string().optional(),
  sellerContact: z.string().optional().transform(capitalizeIfUppercase),
  vehicleYear: z.coerce.number().optional(),
  vehicleMake: z.string().optional().transform(capitalizeIfUppercase),
  vehicleModel: z.string().optional().transform(capitalizeIfUppercase),
  vehicleSalesPrice: z.string().optional().transform((str) => (str ? parseInt(str.replace(/,/g, ''), 10) : undefined)),
  vehicleDownPayment: z.string().optional().transform((str) => (str ? parseInt(str.replace(/,/g, ''), 10) : undefined)),
  notes: z.string().optional(),
  referralSource: z.string().optional().transform(capitalizeIfUppercase),
  referralSourceOther: z.string().optional().transform(capitalizeIfUppercase),

  // step #5
  usCitizen: z.coerce.boolean(),
  usResident: z.coerce.boolean(),
  applicantAgree: z.coerce.boolean(),
  applicantSignature: z.string().optional(),
  coApplicantAgree: z.coerce.boolean().optional(),
  coApplicantSignature: z.string().optional(),
  coApplicantUsCitizen: z.coerce.boolean().optional(),
  coApplicantUsResident: z.coerce.boolean().optional(),

  // dealer info
  dealerId: z.string().optional(),
  dealerLocation: z.string().optional(),
  dealerStock: z.string().optional(),
  dealerName: z.string().optional(),

  leadSource: z.string().optional(),

  // Google
  token: z.string(),

  // Phone application fields
  createdBy: z.string().optional(),
  phoneNumberCallback: z.string().optional(),

  // custom attributes
  customAttributes: z.record(z.string(), z.string()).optional(),

  // analytic name
  analyticName: z.string().optional(),
  analystName: z.string().optional(),

  // Partner service form – partner
  applicationSource: z.string().optional(),
  partner: z.string().optional(),
  partnerAccountId: z.string().optional(),
  partnerRep: z.string().optional().transform(capitalizeIfUppercase),
  partnerRepEmail: z.string().optional().transform(lowercaseIfUppercase),

  // Partner service form – collateral
  // collateralType / unitStatus are only used by TSF (not CCL)
  collateralType: z.string().optional().transform(capitalizeIfUppercase),
  unitStatus: z.string().optional(),

  // Partner service form – back-end products
  gps: moneyStr(),
  theft: moneyStr(),
  tireWheel: moneyStr(),
  vsc: moneyStr(),
  miscDescription: z.string().optional(),
  miscCost: moneyStr(),
});
